import React, { useEffect, useState, useRef } from 'react';
import { debugLog, debugError } from '../../../common/utils/debug';
import {
  DialogContent,
  CircularProgress,
} from '@mui/material';
import {
  DialogContainer,
  DialogHeader,
  DialogTitle,
  DialogText,
} from '../shared';

interface DeviceCheckDialogProps {
  open: boolean;
  onAllChecksPassed: () => void;
  onChecksFailed: (failedChecks: string[]) => void;
}

interface CheckStatus {
  status: 'pending' | 'checking' | 'passed' | 'failed';
  message?: string;
}

interface DeviceChecks {
  browser: CheckStatus;
  microphone: CheckStatus;
  network: CheckStatus;
}

const SUPPORTED_BROWSERS = ['Chrome', 'Edge', 'Edg'];

const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;

  const isActualChrome = !!(window as any).chrome &&
    typeof (window as any).chrome === 'object' &&
    !userAgent.includes('Edg');

  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome') || isActualChrome) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  return 'Unknown';
};

const checkBrowserSupport = (): { supported: boolean; browser: string } => {
  const browser = detectBrowser();

  const isActualChrome = !!(window as any).chrome &&
    typeof (window as any).chrome === 'object';

  const supported = SUPPORTED_BROWSERS.some(b =>
    browser.includes(b) || navigator.userAgent.includes(b)
  ) || isActualChrome;

  return { supported, browser };
};

const checkMicrophoneAccess = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch {
    return false;
  }
};

const checkNetworkStability = async (): Promise<boolean> => {
  const MIN_MEGABITS_PER_SEC = 1;
  const MIN_MBPS = MIN_MEGABITS_PER_SEC / 8;

  const SPEED_TEST_URL = `${window.location.origin}/speed-test.bin?ts=${Date.now()}`;

  try {
    const connection = (navigator as any).connection as
      | { effectiveType?: string; downlink?: number }
      | undefined;

    const effectiveType = connection?.effectiveType;

    if (typeof connection?.downlink === "number") {
      const downlinkMbps = connection.downlink;
      const meetsSpeed = downlinkMbps >= MIN_MEGABITS_PER_SEC;

      debugLog('[Download Speed] - ' + downlinkMbps);
      if (effectiveType === "slow-2g" || effectiveType === "2g") {
        debugLog('[Is 2g ] - ' + true);
        return false;
      }
      return meetsSpeed;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);

    const start = performance.now();
    const res = await fetch(SPEED_TEST_URL, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    });

    if (!res.ok) {
      clearTimeout(timeout);
      return false;
    }

    const blob = await res.blob();
    const end = performance.now();
    clearTimeout(timeout);

    const seconds = (end - start) / 1000;
    const bytes = blob.size;

    const mbPerSec = bytes / seconds / (1024 * 1024);

    const meetsSpeed = mbPerSec >= MIN_MBPS;

    if (effectiveType === "slow-2g" || effectiveType === "2g") {
      return false;
    }

    return meetsSpeed;
  } catch(e) {
    debugLog('[Failed check ] - ' + e);
    return true;
  }
};

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clipPath="url(#clip0_297_2075)">
      <path d="M3.40375 12.5962C2.82875 12.0212 3.21 10.8131 2.9175 10.1056C2.61375 9.375 1.5 8.78125 1.5 8C1.5 7.21875 2.61375 6.625 2.9175 5.89437C3.21 5.1875 2.82875 3.97875 3.40375 3.40375C3.97875 2.82875 5.1875 3.21 5.89437 2.9175C6.62812 2.61375 7.21875 1.5 8 1.5C8.78125 1.5 9.375 2.61375 10.1056 2.9175C10.8131 3.21 12.0212 2.82875 12.5962 3.40375C13.1712 3.97875 12.79 5.18687 13.0825 5.89437C13.3863 6.62812 14.5 7.21875 14.5 8C14.5 8.78125 13.3863 9.375 13.0825 10.1056C12.79 10.8131 13.1712 12.0212 12.5962 12.5962C12.0212 13.1712 10.8131 12.79 10.1056 13.0825C9.375 13.3863 8.78125 14.5 8 14.5C7.21875 14.5 6.625 13.3863 5.89437 13.0825C5.1875 12.79 3.97875 13.1712 3.40375 12.5962Z" fill="#171C27" stroke="#171C27" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.5 8.5L7 10L10.5 6.5" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_297_2075">
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const FailedIcon = () => (
  <div
    style={{
      width: '17px',
      height: '17px',
      borderRadius: '50%',
      backgroundColor: '#E11313'
    }}
  />
);

const SpinnerIcon = () => (
  <CircularProgress size={16} sx={{ color: '#24AEA8' }} />
);

const DeviceCheckDialog: React.FC<DeviceCheckDialogProps> = ({
  open,
  onAllChecksPassed,
  onChecksFailed,
}) => {
  const [checks, setChecks] = useState<DeviceChecks>({
    browser: { status: 'pending' },
    microphone: { status: 'pending' },
    network: { status: 'pending' },
  });
  const [allPassed, setAllPassed] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const callbackInvoked = useRef(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const currentInterviewLink = window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentInterviewLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      debugError('Failed to copy link:', err);
    }
  };

  useEffect(() => {
    if (allPassed && countdown === null) {
      setCountdown(5);
    }

    if (countdown !== null && countdown > 0) {
      countdownIntervalRef.current = setTimeout(() => {
        setCountdown(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
    }

    if (countdown === 0 && !callbackInvoked.current) {
      callbackInvoked.current = true;
      onAllChecksPassed();
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearTimeout(countdownIntervalRef.current);
      }
    };
  }, [allPassed, countdown, onAllChecksPassed]);

  useEffect(() => {
    if (!open) {
      setChecks({
        browser: { status: 'pending' },
        microphone: { status: 'pending' },
        network: { status: 'pending' },
      });
      setAllPassed(false);
      setCountdown(null);
      setLinkCopied(false);
      callbackInvoked.current = false;
      if (countdownIntervalRef.current) {
        clearTimeout(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    const runChecks = async () => {
      setChecks(prev => ({ ...prev, browser: { status: 'checking' } }));
      await new Promise(resolve => setTimeout(resolve, 500));

      const { supported, browser } = checkBrowserSupport();
      setChecks(prev => ({
        ...prev,
        browser: {
          status: supported ? 'passed' : 'failed',
          message: supported ? `${browser} detected` : `${browser} is not supported. Please use Chrome or Edge.`,
        },
      }));

      if (!supported) {
        onChecksFailed(['browser']);
        return;
      }

      setChecks(prev => ({ ...prev, microphone: { status: 'checking' } }));
      await new Promise(resolve => setTimeout(resolve, 500));

      const micAccess = await checkMicrophoneAccess();
      setChecks(prev => ({
        ...prev,
        microphone: {
          status: micAccess ? 'passed' : 'failed',
          message: micAccess ? 'Microphone access granted' : 'Microphone access denied. Please allow microphone access.',
        },
      }));

      if (!micAccess) {
        onChecksFailed(['microphone']);
        return;
      }

      setChecks(prev => ({ ...prev, network: { status: 'checking' } }));
      await new Promise(resolve => setTimeout(resolve, 500));

      const networkStable = await checkNetworkStability();
      setChecks(prev => ({
        ...prev,
        network: {
          status: networkStable ? 'passed' : 'failed',
          message: networkStable ? 'Network connection stable' : 'Network connection unstable. Please check your connection.',
        },
      }));

      if (!networkStable) {
        onChecksFailed(['network']);
        return;
      }

      debugLog('[DeviceCheck] All checks passed! Starting 5 second countdown...');
      setAllPassed(true);
    };

    runChecks();
  }, [open, onAllChecksPassed]);

  const getStatusColor = (status: CheckStatus['status']) => {
    switch (status) {
      case 'pending':
        return '#63615C';
      case 'checking':
        return '#24AEA8';
      case 'passed':
        return '#171C27';
      case 'failed':
        return '#E11313';
    }
  };

  const renderCheckIcon = (status: CheckStatus['status']) => {
    switch (status) {
      case 'pending':
        return null;
      case 'checking':
        return <SpinnerIcon />;
      case 'passed':
        return <SuccessIcon />;
      case 'failed':
        return <FailedIcon />;
    }
  };

  const renderCheckItem = (label: string, check: CheckStatus) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      }}
    >
      <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {renderCheckIcon(check.status)}
      </div>
      <span
        style={{
          color: getStatusColor(check.status),
          fontFamily: 'Interstate',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: 'normal',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </span>
    </div>
  );

  return (
    <DialogContainer open={open}>
      <DialogHeader variant="simple" />

      <div
        style={{
          padding: '16px',
          borderTop: '1px solid #F3F4F6',
          borderBottom: '1px solid #F3F4F6',
          background: 'rgba(0, 0, 0, 0.00)',
        }}
      >
        <DialogTitle style={{ marginBottom: '8px' }}>
          Device Compatibility Check
        </DialogTitle>
        <DialogText style={{ marginBottom: 0 }}>
          We're making sure your device is ready for the interview.
        </DialogText>

        <DialogContent sx={{ paddingBottom: 0 }}>
          {renderCheckItem('Browser Compatibility', checks.browser)}
          {renderCheckItem('Microphone Access', checks.microphone)}
          {renderCheckItem('Network Connection', checks.network)}
        </DialogContent>
      </div>

      {/* Success Footer */}
      {allPassed && countdown !== null && (
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid #F3F4F6',
            textAlign: 'center',
            color: 'var(--color-charcoal, #63615C)',
            fontFamily: 'Interstate',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '20px',
          }}
        >
          Starting session in {countdown}...
        </div>
      )}

      {/* Browser Failed Footer */}
      {checks.browser.status === 'failed' && (
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid #F3F4F6',
            textAlign: 'center',
          }}
        >
          <DialogText centered style={{ marginBottom: '16px' }}>
            Use a supported browser such as<br />Chrome or Microsoft Edge (latest version).
          </DialogText>
          <div
            style={{
              color: 'var(--color-charcoal, #63615C)',
              fontFamily: 'Interstate',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '1.5',
              marginBottom: '12px',
            }}
          >
            Copy this link and paste it in Chrome:
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#F3F4F6',
              borderRadius: '8px',
              padding: '8px 12px',
            }}
          >
            <div
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
                fontSize: '12px',
                color: '#63615C',
              }}
            >
              {currentInterviewLink}
            </div>
            <button
              onClick={handleCopyLink}
              style={{
                padding: '8px 16px',
                backgroundColor: linkCopied ? '#24AEA8' : '#171C27',
                color: '#FFFFFF',
                fontFamily: 'Interstate',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background-color 0.2s',
              }}
            >
              {linkCopied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      )}

      {/* Microphone Failed Footer */}
      {checks.microphone.status === 'failed' && (
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid #F3F4F6',
            textAlign: 'center',
          }}
        >
          <DialogText centered style={{ marginBottom: '16px' }}>
            Check permissions and hardware, then{' '}
            <span style={{ fontWeight: 700 }}>'Refresh'</span>.
          </DialogText>
          <button
            onClick={() => window.location.reload()}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: '#171C27',
              color: '#FFFFFF',
              fontFamily: 'Interstate',
              fontSize: '14px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Network Failed Footer */}
      {checks.network.status === 'failed' && (
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid #F3F4F6',
            textAlign: 'center',
          }}
        >
          <DialogText centered style={{ marginBottom: '16px' }}>
            Your internet connection is unstable!
            <br />
            Check your connection or switch to a stronger network, then{' '}
            <span style={{ fontWeight: 700 }}>'Refresh'</span>.
          </DialogText>
          <button
            onClick={() => window.location.reload()}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: '#171C27',
              color: '#FFFFFF',
              fontFamily: 'Interstate',
              fontSize: '14px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      )}
    </DialogContainer>
  );
};

export default DeviceCheckDialog;
